import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { draftId, draftSlug } from '../../routing/routeConfiguration';

const AboutCTAEnhancer = () => {
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  useEffect(() => {
    const enhanceAboutCTA = () => {
      let aboutCTASection = document.getElementById('about-cta');
      
      if (!aboutCTASection) {
        // Fallback: look for any section containing "Ready to join us?"
        const allSections = document.querySelectorAll('section');
        allSections.forEach(section => {
          if (section.textContent.includes('Ready to join us?')) {
            aboutCTASection = section;
          }
        });
        if (!aboutCTASection) return;
      }

      // Check if buttons already exist to avoid duplicates
      if (aboutCTASection.querySelector('.aboutCtaButtons') || aboutCTASection.querySelector('[data-cta-buttons="added"]')) {
        return;
      }

      // Try multiple possible selectors for the content container
      let sectionContent = aboutCTASection.querySelector('.SectionContainer_sectionContent__vqzb1');
      if (!sectionContent) {
        sectionContent = aboutCTASection.querySelector('[class*="sectionContent"]');
      }
      if (!sectionContent) {
        sectionContent = aboutCTASection.querySelector('div');
      }
      if (!sectionContent) {
        sectionContent = aboutCTASection;
      }

      // Create button container
      const buttonContainer = document.createElement('div');
      buttonContainer.className = 'aboutCtaButtons';
      buttonContainer.setAttribute('data-cta-buttons', 'added');
      buttonContainer.style.cssText = `
        display: flex;
        flex-direction: column;
        gap: 1rem;
        margin-top: 2rem;
        align-items: center;
        padding: 0 1rem;
        box-sizing: border-box;
      `;
      
      // Add responsive behavior for larger screens
      if (window.innerWidth >= 768) {
        buttonContainer.style.flexDirection = 'row';
        buttonContainer.style.justifyContent = 'center';
        buttonContainer.style.gap = '1.5rem';
        buttonContainer.style.padding = '0';
      }

      // Create "Sell Your Gear" button (now with primary styling)
      const sellGearButton = document.createElement('button');
      sellGearButton.textContent = 'Sell Your Gear';
      // Responsive button styling
      const isMobile = window.innerWidth < 768;
      sellGearButton.style.cssText = `
        min-width: ${isMobile ? '160px' : '200px'};
        width: ${isMobile ? '100%' : 'auto'};
        max-width: ${isMobile ? '280px' : 'none'};
        padding: ${isMobile ? '10px 16px' : '12px 24px'};
        border-radius: 6px;
        font-size: ${isMobile ? '14px' : '16px'};
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        border: none;
        background-color: var(--marketplaceColor);
        color: white;
        font-family: inherit;
        box-sizing: border-box;
      `;
      sellGearButton.onmouseover = () => sellGearButton.style.backgroundColor = 'var(--marketplaceColorDark, var(--marketplaceColor))';
      sellGearButton.onmouseout = () => sellGearButton.style.backgroundColor = 'var(--marketplaceColor)';
      sellGearButton.onclick = () => {
        const newListingPath = createResourceLocatorString(
          'EditListingPage',
          routeConfiguration,
          { slug: draftSlug, id: draftId, type: 'new', tab: 'details' },
          { listingType: 'sell-your-gear' }
        );
        history.push(newListingPath);
      };

      // Create "Find Golf Gear" button (now with secondary styling)
      const findGearButton = document.createElement('button');
      findGearButton.textContent = 'Find Golf Gear';
      findGearButton.style.cssText = `
        min-width: ${isMobile ? '160px' : '200px'};
        width: ${isMobile ? '100%' : 'auto'};
        max-width: ${isMobile ? '280px' : 'none'};
        padding: ${isMobile ? '10px 16px' : '12px 24px'};
        border-radius: 6px;
        font-size: ${isMobile ? '14px' : '16px'};
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        background-color: transparent;
        color: var(--marketplaceColor);
        border: 2px solid var(--marketplaceColor);
        font-family: inherit;
        box-sizing: border-box;
      `;
      findGearButton.onmouseover = () => {
        findGearButton.style.backgroundColor = 'var(--marketplaceColor)';
        findGearButton.style.color = 'white';
      };
      findGearButton.onmouseout = () => {
        findGearButton.style.backgroundColor = 'transparent';
        findGearButton.style.color = 'var(--marketplaceColor)';
      };
      findGearButton.onclick = () => {
        const searchPath = createResourceLocatorString('SearchPage', routeConfiguration, {}, { pub_listingType: 'sell-your-gear' });
        history.push(searchPath);
      };

      // Add buttons to container (Sell Your Gear first, then Find Golf Gear)
      buttonContainer.appendChild(sellGearButton);
      buttonContainer.appendChild(findGearButton);

      // Add container to section
      sectionContent.appendChild(buttonContainer);
    };

    // Run immediately and after a short delay to handle dynamic content
    enhanceAboutCTA();
    const timeoutId = setTimeout(enhanceAboutCTA, 500);
    const timeoutId2 = setTimeout(enhanceAboutCTA, 1000);
    const timeoutId3 = setTimeout(enhanceAboutCTA, 2000);

    // Listen for navigation changes
    const handleNavigation = () => {
      setTimeout(enhanceAboutCTA, 100);
      setTimeout(enhanceAboutCTA, 500);
    };

    // Listen for popstate (back/forward navigation)
    window.addEventListener('popstate', handleNavigation);

    // Listen for DOM changes that might indicate new content
    const observer = new MutationObserver(() => {
      setTimeout(enhanceAboutCTA, 100);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false
    });

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
      window.removeEventListener('popstate', handleNavigation);
      observer.disconnect();
    };
  }, [history, routeConfiguration]);

  return null; // This component doesn't render anything
};

export default AboutCTAEnhancer;